import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Clock, Music } from 'lucide-react';
import type { EventWithBand } from '../../hooks/useSearch';
import { Link } from 'react-router-dom';

interface Props {
  events: EventWithBand[];
  view: 'month' | 'week' | 'day';
  onViewChange: (view: 'month' | 'week' | 'day') => void;
}

function getMonthDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday start
  const days: Date[] = [];

  // Previous month padding
  for (let i = startPad - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }
  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i));
  }
  // Next month padding to fill 6 rows
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push(new Date(year, month + 1, i));
  }
  return days;
}

function getWeekDays(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);

  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }
  return days;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatTimeShort(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CalendarView({ events, view, onViewChange }: Props) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const today = new Date();

  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventWithBand[]>();
    for (const event of events) {
      const key = new Date(event.start_time).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    return map;
  }, [events]);

  function navigate(dir: -1 | 1) {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  }

  function goToday() {
    setCurrentDate(new Date());
    setSelectedDate(null);
  }

  const selectedEvents = selectedDate ? eventsByDate.get(selectedDate.toDateString()) ?? [] : [];

  const headerLabel = view === 'month'
    ? currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : view === 'week'
      ? (() => {
          const days = getWeekDays(currentDate);
          return `${days[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${days[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        })()
      : currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div>
      {/* Calendar header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-800 transition text-gray-400 hover:text-gray-50">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold min-w-[200px] text-center">{headerLabel}</h3>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-gray-800 transition text-gray-400 hover:text-gray-50">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button onClick={goToday} className="ml-2 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1 rounded-lg transition">
            Today
          </button>
        </div>

        <div className="flex bg-gray-800 rounded-lg p-0.5 text-sm">
          {(['month', 'week', 'day'] as const).map((v) => (
            <button
              key={v}
              onClick={() => onViewChange(v)}
              className={`px-3 py-1.5 rounded-md font-medium transition capitalize ${
                view === v ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Month view */}
      {view === 'month' && (
        <div className="border border-gray-800 rounded-xl overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-900">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2 border-b border-gray-800">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {getMonthDays(currentDate.getFullYear(), currentDate.getMonth()).map((day, i) => {
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = sameDay(day, today);
              const dayEvents = eventsByDate.get(day.toDateString()) ?? [];
              const isSelected = selectedDate && sameDay(day, selectedDate);

              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(day)}
                  className={`relative min-h-[80px] sm:min-h-[100px] p-1 sm:p-2 text-left border-b border-r border-gray-800 transition hover:bg-gray-800/50 ${
                    !isCurrentMonth ? 'opacity-30' : ''
                  } ${isSelected ? 'bg-indigo-900/20 ring-1 ring-indigo-500' : ''}`}
                >
                  <span className={`text-xs sm:text-sm font-medium ${
                    isToday ? 'bg-indigo-600 text-white w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center' : 'text-gray-400'
                  }`}>
                    {day.getDate()}
                  </span>
                  {dayEvents.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {dayEvents.slice(0, 2).map((ev) => (
                        <div key={ev.id} className="bg-indigo-900/40 border-l-2 border-indigo-500 text-[10px] sm:text-xs text-indigo-300 px-1 py-0.5 rounded-r truncate">
                          <span className="hidden sm:inline">{formatTimeShort(ev.start_time)} </span>
                          {ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-[10px] text-indigo-400 px-1">+{dayEvents.length - 2} more</div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Week view */}
      {view === 'week' && (
        <div className="border border-gray-800 rounded-xl overflow-hidden">
          <div className="grid grid-cols-7">
            {getWeekDays(currentDate).map((day, i) => {
              const isToday = sameDay(day, today);
              const dayEvents = eventsByDate.get(day.toDateString()) ?? [];
              const isSelected = selectedDate && sameDay(day, selectedDate);

              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(day)}
                  className={`min-h-[200px] p-2 text-left border-r border-gray-800 last:border-r-0 transition hover:bg-gray-800/50 ${
                    isSelected ? 'bg-indigo-900/20 ring-1 ring-indigo-500' : ''
                  }`}
                >
                  <div className="text-center mb-2">
                    <div className="text-[10px] uppercase text-gray-500 font-semibold">{WEEKDAY_LABELS[i]}</div>
                    <div className={`text-sm font-medium mt-0.5 ${
                      isToday ? 'bg-indigo-600 text-white w-7 h-7 rounded-full flex items-center justify-center mx-auto' : 'text-gray-300'
                    }`}>
                      {day.getDate()}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {dayEvents.map((ev) => (
                      <div key={ev.id} className="bg-indigo-900/40 border-l-2 border-indigo-500 text-[10px] sm:text-xs text-indigo-300 px-1.5 py-1 rounded-r">
                        <div className="font-medium truncate">{ev.title}</div>
                        <div className="text-indigo-400">{formatTimeShort(ev.start_time)}</div>
                        {ev.venue_name && <div className="text-gray-400 truncate">{ev.venue_name}</div>}
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Day view */}
      {view === 'day' && (() => {
        const dayEvents = eventsByDate.get(currentDate.toDateString()) ?? [];
        return (
          <div className="border border-gray-800 rounded-xl overflow-hidden">
            {dayEvents.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Music className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No gigs on this day</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {dayEvents.map((ev) => (
                  <Link
                    key={ev.id}
                    to={`/band/${ev.band?.slug}`}
                    className="flex items-start gap-4 p-4 hover:bg-gray-800/50 transition"
                  >
                    <div className="bg-indigo-600/20 border border-indigo-500/30 rounded-lg px-3 py-2 text-center flex-shrink-0 min-w-[60px]">
                      <div className="text-indigo-300 text-lg font-bold">{formatTimeShort(ev.start_time)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-50">{ev.title}</h4>
                      <div className="flex items-center gap-1 text-gray-400 text-sm mt-0.5">
                        <Music className="w-3 h-3" />
                        {ev.band?.name}
                      </div>
                      {ev.venue_name && (
                        <div className="flex items-center gap-1 text-gray-500 text-sm mt-0.5">
                          <MapPin className="w-3 h-3" />
                          {ev.venue_name}
                          {ev.venue_address && ` - ${ev.venue_address}`}
                        </div>
                      )}
                      {ev.description && (
                        <p className="text-gray-500 text-sm mt-1 line-clamp-2">{ev.description}</p>
                      )}
                      {ev.ticket_url && (
                        <span className="inline-block mt-1.5 text-indigo-400 text-xs font-medium">
                          Tickets available {ev.ticket_price && `(${ev.ticket_price})`}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Selected day detail panel (month/week views) */}
      {(view === 'month' || view === 'week') && selectedDate && (
        <div className="mt-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
            {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </h4>
          {selectedEvents.length === 0 ? (
            <p className="text-gray-500 text-sm py-4 text-center">No gigs on this day</p>
          ) : (
            <div className="space-y-3">
              {selectedEvents.map((ev) => (
                <Link
                  key={ev.id}
                  to={`/band/${ev.band?.slug}`}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-800 transition"
                >
                  {ev.band?.logo_url ? (
                    <img src={ev.band.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <Music className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h5 className="font-medium text-gray-50 truncate">{ev.title}</h5>
                      <span className="text-indigo-400 text-xs font-medium flex-shrink-0">{ev.band?.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400 text-sm mt-0.5">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeShort(ev.start_time)}
                      </span>
                      {ev.venue_name && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          {ev.venue_name}
                        </span>
                      )}
                    </div>
                    {ev.description && (
                      <p className="text-gray-500 text-xs mt-1 line-clamp-1">{ev.description}</p>
                    )}
                    {ev.ticket_url && (
                      <span className="text-indigo-400 text-xs mt-1 inline-block">
                        Tickets {ev.ticket_price && `(${ev.ticket_price})`}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
